/* Defy The Norm / product page: variants, gallery, add to cart, sticky bar, shipping bar, delivery dates, kit. */

function dtnMoney(cents, format) {
  const fmt = format || '${{amount}}';
  const n = (Number(cents) || 0) / 100;
  const group = (str, sep) => str.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  const two = (sep, dec) => { const [a, b] = n.toFixed(2).split('.'); return group(a, sep) + dec + b; };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
    switch (key) {
      case 'amount_no_decimals': return group(Math.round(n).toString(), ',');
      case 'amount_with_comma_separator': return two('.', ',');
      case 'amount_no_decimals_with_comma_separator': return group(Math.round(n).toString(), '.');
      case 'amount_with_apostrophe_separator': return two("'", '.');
      default: return two(',', '.');
    }
  });
}

class DtnProduct extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    try { this.data = JSON.parse(this.querySelector('[data-dtn-pdp-json]').textContent); } catch (e) { this.data = { variants: [], options: [], text: {} }; }
    this.text = this.data.text || {};
    this.form = this.querySelector('form.dtn-pdp__form');
    this.atc = this.querySelector('[data-atc]');
    this.qtyInput = this.querySelector('[data-qty]');
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.cartTotal = Number(this.dataset.cartTotal) || 0;

    const id = Number(this.querySelector('[data-variant-input]')?.value);
    this.variant = this.data.variants.find((v) => v.id === id) || this.data.variants[0] || null;

    this.initOptions();
    this.initGallery();
    this.initQty();
    this.initForm();
    this.initKit();
    this.initDialogs();
    this.initSticky();
    this.initDelivery();
    this.update(false);
  }

  /* ---------- Variants ---------- */
  initOptions() {
    this.addEventListener('change', (e) => {
      if (e.target.matches('input[data-option-index]')) {
        this._lastIndex = Number(e.target.dataset.optionIndex);
        this.onOptionChange();
      }
    });
  }
  selectedOptions() {
    return [...this.querySelectorAll('.dtn-pdp__opt')].map((fs) => fs.querySelector('input:checked')?.value);
  }
  onOptionChange() {
    const sel = this.selectedOptions();
    let v = this.data.variants.find((x) => x.options.every((o, i) => o === sel[i]));
    if (!v) {
      // That combination does not exist: keep the option just picked, match as many others as possible
      const li = this._lastIndex || 0;
      let best = null; let bestScore = -1;
      this.data.variants.forEach((x) => {
        if (x.options[li] !== sel[li]) return;
        const score = (x.available ? 100 : 0) + x.options.filter((o, i) => o === sel[i]).length;
        if (score > bestScore) { best = x; bestScore = score; }
      });
      v = best;
      if (v) {
        this.querySelectorAll('.dtn-pdp__opt').forEach((fs, oi) => {
          fs.querySelectorAll('input[data-option-index]').forEach((input) => { input.checked = input.value === v.options[oi]; });
        });
      }
    }
    this.variant = v || null;
    this.update(true);
  }
  update(fromUser) {
    const v = this.variant;
    const t = this.text;
    const money = (c) => dtnMoney(c, this.data.moneyFormat);

    // Selected labels and availability marks
    const sel = this.selectedOptions();
    this.querySelectorAll('.dtn-pdp__opt').forEach((fs, oi) => {
      const checked = fs.querySelector('input:checked');
      const label = fs.querySelector('[data-option-value]');
      if (label && checked) label.textContent = checked.value;
      fs.querySelectorAll('input[data-option-index]').forEach((input) => {
        const ok = this.data.variants.some((x) => x.available && x.options[oi] === input.value && x.options.every((o, i) => i === oi || sel[i] === undefined || o === sel[i]));
        input.nextElementSibling?.classList.toggle('is-out', !ok);
      });
    });

    // Variant id inputs
    this.querySelectorAll('[data-variant-input]').forEach((input) => {
      input.value = v ? v.id : '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Price
    const priceEl = this.querySelector('[data-price]');
    const compareEl = this.querySelector('[data-compare]');
    if (v && priceEl) priceEl.textContent = money(v.price);
    if (compareEl) {
      const onSale = v && v.compare > v.price;
      compareEl.hidden = !onSale;
      if (onSale) compareEl.textContent = money(v.compare);
    }
    const saleBadge = this.querySelector('[data-sale-badge]');
    if (saleBadge && v) {
      const onSale = v.compare > v.price;
      saleBadge.hidden = !onSale;
      if (onSale) saleBadge.textContent = Math.round((v.compare - v.price) * 100 / v.compare) + '% off';
    }

    // Button
    if (this.atc) {
      const label = this.atc.querySelector('[data-atc-label]');
      const avail = !!(v && v.available);
      this.atc.disabled = !avail;
      if (label) {
        if (!v) label.textContent = t.unavailable || 'Unavailable';
        else if (!avail) label.textContent = t.soldOut || 'Sold out';
        else {
          const hasPrice = this.atc.dataset.priceInButton === 'true';
          label.innerHTML = '';
          label.append(document.createTextNode(t.add || 'Add to cart'));
          if (hasPrice) {
            const sep = document.createElement('span'); sep.className = 'dtn-pdp__atc-sep'; sep.textContent = ' / ';
            const pr = document.createElement('span'); pr.dataset.atcPrice = ''; pr.textContent = money(v.price * this.qty());
            label.append(sep, pr);
          }
        }
      }
    }

    // Stock message
    const stock = this.querySelector('[data-stock]');
    if (stock) {
      stock.innerHTML = '';
      const low = Number(this.dataset.lowStock) || 0;
      if (v && v.available && v.tracked && low > 0 && v.qty > 0 && v.qty <= low) {
        const dot = document.createElement('span'); dot.className = 'dtn-pdp__stock-dot';
        const msg = document.createElement('strong');
        msg.textContent = (t.low || 'Only [qty] left.').replace('[qty]', v.qty).replace('[variant]', v.title);
        stock.append(dot, msg);
      } else if (v && v.available && t.inStock) {
        const dot = document.createElement('span'); dot.className = 'dtn-pdp__stock-dot dtn-pdp__stock-dot--ok';
        const msg = document.createElement('span'); msg.textContent = t.inStock;
        stock.append(dot, msg);
      }
    }

    // Media
    if (fromUser && v && v.media) {
      const idx = this.slides.findIndex((s) => Number(s.dataset.mediaId) === v.media);
      if (idx > -1) this.goTo(idx);
    }

    // URL
    if (fromUser && v && this.dataset.url) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
    }

    // Sticky bar
    const sv = this.querySelector('[data-sticky-variant]');
    const sp = this.querySelector('[data-sticky-price]');
    const si = this.querySelector('[data-sticky-img]');
    const sb = this.querySelector('[data-sticky-add]');
    if (sv && v && v.title !== 'Default Title') sv.textContent = v.title + ' / ';
    if (sp && v) sp.textContent = money(v.price);
    if (si && v && v.img) si.src = v.img;
    if (sb) { sb.disabled = !(v && v.available); sb.textContent = v && v.available ? (t.add || 'Add to cart') : (t.soldOut || 'Sold out'); }

    this.updateShipping();
    this.syncKitSizes();
    this.updateKitTotal();
  }

  /* ---------- Gallery ---------- */
  initGallery() {
    if (!this.track || !this.slides.length) return;
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.dots = [...this.querySelectorAll('.dtn-pdp__dots i')];
    this.countNow = this.querySelector('[data-count-now]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.index = 0;
    this.thumbs.forEach((b) => b.addEventListener('click', () => this.goTo(Number(b.dataset.thumb))));
    this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1));
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
    });
    let raf = 0;
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (Date.now() - (this._jumpAt || 0) < 700) return;
        const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
        if (i !== this.index) this.setActive(i);
      });
    }, { passive: true });
    this.track.addEventListener('click', (e) => {
      const img = e.target.closest('img[data-zoom-index]');
      if (img && window.matchMedia('(min-width: 990px)').matches) this.openZoom(Number(img.dataset.zoomIndex));
    });
    // Start on the selected variant's image
    if (this.variant && this.variant.media) {
      const idx = this.slides.findIndex((s) => Number(s.dataset.mediaId) === this.variant.media);
      if (idx > 0) { this.track.style.scrollBehavior = 'auto'; this.goTo(idx); requestAnimationFrame(() => { this.track.style.scrollBehavior = ''; }); }
    }
    this.setActive(this.index);
  }
  goTo(i) {
    if (!this.track) return;
    const n = this.slides.length;
    const idx = Math.max(0, Math.min(n - 1, i));
    this._jumpAt = Date.now();
    this.track.scrollTo({ left: idx * this.track.clientWidth });
    this.setActive(idx);
  }
  setActive(i) {
    this.index = i;
    (this.thumbs || []).forEach((b, k) => b.setAttribute('aria-current', k === i ? 'true' : 'false'));
    (this.dots || []).forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
    if (this.countNow) this.countNow.textContent = String(i + 1).padStart(2, '0');
    if (this.prevBtn) this.prevBtn.disabled = i <= 0;
    if (this.nextBtn) this.nextBtn.disabled = i >= this.slides.length - 1;
    const active = this.thumbs?.[i];
    if (active && active.parentElement) {
      const p = active.parentElement;
      const top = active.offsetTop - p.offsetTop;
      if (top < p.scrollTop || top + active.offsetHeight > p.scrollTop + p.clientHeight) p.scrollTo({ top: top - 10, behavior: 'smooth' });
    }
    this.slides.forEach((s, k) => { if (k !== i) s.querySelectorAll('video').forEach((vid) => vid.pause()); });
  }

  /* ---------- Quantity ---------- */
  qty() { return Math.max(1, parseInt(this.qtyInput?.value, 10) || 1); }
  initQty() {
    this.querySelectorAll('[data-qty-step]').forEach((b) => b.addEventListener('click', () => {
      if (!this.qtyInput) return;
      this.qtyInput.value = Math.max(1, this.qty() + Number(b.dataset.qtyStep));
      this.update(false);
    }));
    this.qtyInput?.addEventListener('change', () => { this.qtyInput.value = this.qty(); this.update(false); });
  }

  /* ---------- Add to cart ---------- */
  initForm() {
    if (!this.form) return;
    this.error = this.querySelector('[data-error]');
    this.form.addEventListener('submit', (e) => { e.preventDefault(); this.add(); });
    this.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
      if (this.selectedOptions().some((o) => o === undefined)) { this.atc?.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
      this.add();
    });
  }
  kitItems() {
    return [...this.querySelectorAll('[data-kit-input]:checked')].map((i) => ({ id: Number(i.value), quantity: 1 }));
  }
  async add() {
    const v = this.variant;
    if (!v || !v.available) return;
    const items = [{ id: v.id, quantity: this.qty() }, ...this.kitItems()];
    const btns = [this.atc, this.querySelector('[data-sticky-add]')].filter(Boolean);
    btns.forEach((b) => b.setAttribute('aria-busy', 'true'));
    if (this.error) this.error.textContent = '';
    try {
      let Cart = null;
      try { ({ Cart } = await import('@troop-themes/main')); } catch (err) { Cart = null; }
      if (Cart && typeof Cart.addItems === 'function') {
        await Cart.addItems({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items, sections: Cart.sections })
        }, this.atc || true);
      } else {
        const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error(res.statusText);
        window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
        return;
      }
      this.querySelectorAll('[data-kit-input]:checked').forEach((i) => { i.checked = false; });
      this.refreshCartTotal();
    } catch (err) {
      if (this.error) this.error.textContent = this.text.error || 'Something went wrong. Please try again.';
    } finally {
      btns.forEach((b) => b.removeAttribute('aria-busy'));
      this.update(false);
    }
  }
  async refreshCartTotal() {
    try {
      const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js');
      const c = await res.json();
      this.cartTotal = c.total_price || 0;
      this.updateShipping();
    } catch (e) { /* keep the old total */ }
  }

  /* ---------- Free shipping bar ---------- */
  updateShipping() {
    const box = this.querySelector('[data-ship]');
    const limit = Number(this.dataset.shipLimit) || 0;
    if (!box || !limit) return;
    const line = this.variant ? this.variant.price * this.qty() : 0;
    const kit = this.kitItems().length ? [...this.querySelectorAll('[data-kit-input]:checked')].reduce((a, i) => a + Number(i.dataset.price || 0), 0) : 0;
    const total = this.cartTotal + line + kit;
    const left = limit - total;
    const textEl = box.querySelector('[data-ship-text]');
    const bar = box.querySelector('[data-ship-bar]');
    const done = left <= 0;
    box.dataset.done = done ? 'true' : 'false';
    if (textEl) {
      if (done) textEl.textContent = this.text.shipDone || 'Free shipping unlocked';
      else {
        const parts = (this.text.shipAway || 'You are [amount] away from free shipping').split('[amount]');
        textEl.textContent = '';
        textEl.append(document.createTextNode(parts[0] || ''));
        const strong = document.createElement('strong'); strong.textContent = dtnMoney(left, this.data.moneyFormat);
        textEl.append(strong, document.createTextNode(parts[1] || ''));
      }
    }
    if (bar) bar.style.setProperty('--p', Math.min(100, Math.round(total / limit * 100)) + '%');
  }

  /* ---------- Kit ---------- */
  initKit() {
    this.kit = this.querySelector('[data-kit]');
    if (!this.kit) return;
    this.kit.addEventListener('change', (e) => {
      const sel = e.target.closest('[data-kit-var]');
      if (sel) { if (e.isTrusted) sel.dataset.touched = '1'; this.applyKitVariant(sel); sel.closest('[data-kit-item]').querySelector('[data-kit-input]').checked = true; }
      if (sel || e.target.matches('[data-kit-input]')) { this.updateKitTotal(); this.updateShipping(); }
    });
    this.kit.addEventListener('click', (e) => {
      const cta = e.target.closest('[data-kit-add]');
      if (!cta) return;
      e.preventDefault();
      if (!this.variant || !this.variant.available) return;
      if (this.selectedOptions().some((o) => o === undefined)) {
        this.querySelector('[data-picker], .dtn-pdp__picker, [data-atc]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const inputs = [...this.kit.querySelectorAll('[data-kit-input]')];
      if (!inputs.some((i) => i.checked)) inputs.forEach((i) => { i.checked = true; });
      this.updateKitTotal();
      this.add();
    });
    if (this.kit.hasAttribute('data-kit-auto')) this.loadKit();
    else { this.syncKitSizes(); this.updateKitTotal(); }
  }
  applyKitVariant(sel) {
    const row = sel.closest('[data-kit-item]');
    const opt = sel.selectedOptions[0];
    const input = row.querySelector('[data-kit-input]');
    input.value = opt.value;
    input.dataset.price = opt.dataset.price;
    const price = row.querySelector('[data-kit-price]');
    if (price) price.textContent = dtnMoney(opt.dataset.price, this.data.moneyFormat);
  }
  /* Add-ons with sizes follow the size picked for the main product when they have it */
  syncKitSizes() {
    if (!this.kit || !this.variant) return;
    const mine = this.variant.options || [];
    this.kit.querySelectorAll('[data-kit-var]').forEach((sel) => {
      if (sel.dataset.touched) return;
      const match = [...sel.options].find((o) => (o.dataset.options || o.textContent).split(/\s*[|/]\s*/).some((x) => mine.includes(x.trim())));
      if (match && sel.value !== match.value) { sel.value = match.value; this.applyKitVariant(sel); }
    });
  }
  async loadKit() {
    const kit = this.kit;
    const max = Number(kit.dataset.kitMax) || 3;
    const myType = (kit.dataset.kitType || '').toLowerCase();
    const myId = Number(this.data.productId) || 0;
    const root = window.Shopify?.routes?.root || '/';
    const toCents = (p) => (typeof p === 'string' && p.includes('.') ? Math.round(parseFloat(p) * 100) : Number(p) || 0);
    const clean = (t) => String(t || '').split(' | ')[0];
    const norm = (p) => {
      const variants = (p.variants || []).filter((v) => v.available !== false).map((v) => ({
        id: Number(v.id), title: v.title, price: toCents(v.price), options: v.options || String(v.title || '').split(' / ')
      }));
      const img = p.featured_image?.url || p.featured_image || p.image || null;
      return { id: Number(p.id), title: clean(p.title), type: p.type || p.product_type || '', url: p.url, image: typeof img === 'string' ? img : null, variants, gift: /gift.?card/i.test((p.type || '') + ' ' + (p.handle || '')) };
    };
    const lists = [];
    const term = (kit.dataset.kitTerm || '').trim();
    const get = async (url, pick) => { try { const r = await fetch(url, { headers: { Accept: 'application/json' } }); if (!r.ok) return []; return (pick(await r.json()) || []).map(norm); } catch (e) { return []; } };
    const [family, related] = await Promise.all([
      term.length > 2 ? get(`${root}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=hide&resources[options][fields]=title`, (j) => j?.resources?.results?.products) : [],
      myId ? get(`${root}recommendations/products.json?product_id=${myId}&limit=10&intent=related`, (j) => j?.products) : []
    ]);
    let fallback = [];
    try { fallback = JSON.parse(kit.querySelector('[data-kit-fallback]')?.textContent || '[]').map(norm); } catch (e) { fallback = []; }
    lists.push(family, related, fallback);

    const list = kit.querySelector('[data-kit-list]');
    const kept = [...list.querySelectorAll('[data-kit-item]')];
    const seen = new Set([myId, ...kept.map((r) => Number(r.dataset.productId) || 0)]);
    const pool = [];
    lists.forEach((l) => l.forEach((p) => {
      if (!p.id || seen.has(p.id) || p.gift || !p.variants.length || p.variants.every((v) => !v.price)) return;
      seen.add(p.id); pool.push(p);
    }));
    // Same kit on every product page: one of each gear type from the fallback list (flag, hat,
    // sticker...), never the type being viewed, matching the design line first when possible.
    const typeOf = (t) => String(t || '').toLowerCase().replace(/s$/, '').trim();
    const mine = typeOf(myType);
    const keptTypes = new Set(kept.map((r) => typeOf(r.dataset.type)));
    const slots = [];
    fallback.forEach((p) => { const t = typeOf(p.type); if (t && t !== mine && !slots.includes(t) && !keptTypes.has(t)) slots.push(t); });
    const need = Math.max(0, max - kept.length);
    const picked = [];
    slots.forEach((t) => { if (picked.length >= need) return; const hit = pool.find((p) => typeOf(p.type) === t && !picked.includes(p)); if (hit) picked.push(hit); });
    const taken = new Set([...keptTypes, ...picked.map((p) => typeOf(p.type))]);
    pool.forEach((p) => { if (picked.length < need && typeOf(p.type) !== mine && !taken.has(typeOf(p.type)) && !picked.includes(p)) { picked.push(p); taken.add(typeOf(p.type)); } });
    pool.forEach((p) => { if (picked.length < need && typeOf(p.type) !== mine && !picked.includes(p)) picked.push(p); });
    pool.forEach((p) => { if (picked.length < need && !picked.includes(p)) picked.push(p); });

    list.querySelectorAll('.dtn-pdp__kit-ghost').forEach((g) => g.remove());
    if (!kept.length && !picked.length) { kit.remove(); this.kit = null; return; }
    picked.forEach((p) => list.append(this.kitRow(p)));
    kit.removeAttribute('data-loading');
    this.updateKitTotal();
    this.syncKitSizes();
  }
  kitRow(p) {
    const money = (c) => dtnMoney(c, this.data.moneyFormat);
    const v = p.variants[0];
    const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
    const row = el('label', 'dtn-pdp__kit-item'); row.setAttribute('data-kit-item', ''); row.dataset.productId = p.id; row.dataset.type = p.type || '';
    const input = el('input'); input.type = 'checkbox'; input.value = v.id; input.dataset.price = v.price; input.setAttribute('data-kit-input', '');
    const box = el('span', 'dtn-pdp__kit-box'); box.setAttribute('aria-hidden', 'true');
    box.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12l5 5 9-10"/></svg>';
    const media = el('span', 'dtn-pdp__kit-img');
    if (p.image) { const img = el('img'); img.src = p.image.startsWith('data:') ? p.image : p.image.replace(/^(https?:)?\/\//, 'https://') + (p.image.includes('?') ? '&' : '?') + 'width=140'; img.alt = ''; img.loading = 'lazy'; img.width = 70; img.height = 70; media.append(img); }
    const name = el('span', 'dtn-pdp__kit-name');
    const b = el('b'); const a = el('a', null, p.title); a.href = p.url; b.append(a); name.append(b);
    const multi = p.variants.length > 1 || (p.variants.length === 1 && v.title && v.title !== 'Default Title');
    if (p.variants.length > 1) {
      const sel = el('select', 'dtn-pdp__kit-var'); sel.setAttribute('data-kit-var', '');
      sel.setAttribute('aria-label', 'Option for ' + p.title);
      p.variants.forEach((x) => { const o = el('option', null, x.title); o.value = x.id; o.dataset.price = x.price; o.dataset.options = (x.options || []).join('|'); sel.append(o); });
      name.append(sel);
    } else {
      name.append(el('span', null, multi ? v.title : (this.text.kitOneSize || 'Add to your order')));
    }
    const price = el('span', 'dtn-pdp__kit-price', money(v.price)); price.setAttribute('data-kit-price', '');
    row.append(input, box, media, name, price);
    return row;
  }
  updateKitTotal() {
    const out = this.querySelector('[data-kit-total]');
    const cta = this.querySelector('[data-kit-add]');
    const all = [...this.querySelectorAll('[data-kit-input]')];
    const picked = all.filter((i) => i.checked);
    const sum = (list) => list.reduce((a, i) => a + Number(i.dataset.price || 0), 0);
    const main = this.variant ? this.variant.price * this.qty() : 0;
    if (out) {
      out.textContent = '';
      if (picked.length && this.variant) {
        const parts = (this.text.kitTotal || 'Bundle total: [total] for [count] items').split('[total]');
        out.append(document.createTextNode(parts[0]));
        const strong = document.createElement('strong'); strong.textContent = dtnMoney(main + sum(picked), this.data.moneyFormat);
        out.append(strong, document.createTextNode((parts[1] || '').replace('[count]', this.qty() + picked.length)));
      }
    }
    if (cta) {
      const use = picked.length ? picked : all;
      const count = this.qty() + use.length;
      const label = (picked.length ? (this.text.kitCta || 'Add [count] items to cart') : (cta.dataset.allLabel || this.text.kitCtaAll || 'Add the full kit')).replace('[count]', count);
      const txt = cta.querySelector('[data-kit-cta-text]');
      const price = cta.querySelector('[data-kit-cta-price]');
      if (txt) txt.textContent = label;
      if (price) price.textContent = this.variant ? dtnMoney(main + sum(use), this.data.moneyFormat) : '';
      const off = !this.variant || !this.variant.available || !all.length;
      cta.disabled = off;
      cta.hidden = !all.length;
    }
  }

  /* ---------- Dialogs: size guide and zoom ---------- */
  initDialogs() {
    this.querySelectorAll('[data-dialog-open]').forEach((b) => b.addEventListener('click', () => {
      const d = document.getElementById(b.dataset.dialogOpen);
      if (d && typeof d.showModal === 'function') d.showModal();
    }));
    this.querySelectorAll('dialog').forEach((d) => {
      d.querySelectorAll('[data-dialog-close]').forEach((b) => b.addEventListener('click', () => d.close()));
      d.addEventListener('click', (e) => { if (e.target === d) d.close(); });
      if (d.parentElement !== document.body && !d.hasAttribute('data-zoom')) document.body.appendChild(d);
    });
    this.zoom = this.querySelector('[data-zoom]');
    this.querySelector('[data-zoom-open]')?.addEventListener('click', () => this.openZoom(this.index || 0));
  }
  openZoom(i) {
    const d = this.zoom;
    if (!d || typeof d.showModal !== 'function') return;
    d.showModal();
    const img = d.querySelector(`[data-zoom-img="${i}"]`);
    if (img) requestAnimationFrame(() => img.scrollIntoView({ block: 'start' }));
  }

  /* ---------- Sticky add to cart ---------- */
  initSticky() {
    this.sticky = this.querySelector('[data-sticky]');
    if (!this.sticky || !this.atc || !('IntersectionObserver' in window)) return;
    const mq = window.matchMedia('(max-width: 989px)');
    const footer = document.querySelector('.layout--footer, footer');
    let atcVisible = true; let atcAbove = false; let footerVisible = false;
    const render = () => {
      const show = !atcVisible && !footerVisible && (mq.matches || atcAbove);
      this.sticky.dataset.show = show ? 'true' : 'false';
      this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      this.sticky.querySelector('[data-sticky-add]')?.setAttribute('tabindex', show ? '0' : '-1');
    };
    new IntersectionObserver(([e]) => {
      atcVisible = e.isIntersecting;
      atcAbove = !e.isIntersecting && e.boundingClientRect.top < 0;
      render();
    }, { rootMargin: '-60px 0px 0px 0px' }).observe(this.atc);
    if (footer) new IntersectionObserver(([e]) => { footerVisible = e.isIntersecting; render(); }).observe(footer);
    mq.addEventListener?.('change', render);
  }

  /* ---------- Delivery estimate ---------- */
  initDelivery() {
    const el = this.querySelector('[data-delivery]');
    if (!el) return;
    const min = Number(el.dataset.min) || 2;
    const max = Math.max(min, Number(el.dataset.max) || min);
    const shipDays = (el.dataset.shipDays || '').split(',').map((d) => parseInt(d, 10)).filter((d) => d >= 0 && d <= 6);
    const lang = document.documentElement.lang || 'en';
    const fDay = new Intl.DateTimeFormat(lang, { weekday: 'long' });
    const fDate = new Intl.DateTimeFormat(lang, { weekday: 'short', month: 'short', day: 'numeric' });
    // Next shipping day after today (any weekday when none are set)
    const ship = new Date(); ship.setHours(12, 0, 0, 0);
    for (let k = 0; k < 8; k++) {
      ship.setDate(ship.getDate() + 1);
      const d = ship.getDay();
      if (shipDays.length ? shipDays.includes(d) : (d !== 0 && d !== 6)) break;
    }
    const addBusiness = (from, n) => {
      const d = new Date(from); let added = 0;
      while (added < n) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) added++; }
      return d;
    };
    const range = fDate.format(addBusiness(ship, min)) + ' to ' + fDate.format(addBusiness(ship, max));
    const out = el.querySelector('[data-delivery-text]');
    if (!out) return;
    out.textContent = '';
    (el.dataset.text || '[dates]').split(/(\[ship\]|\[dates\])/).forEach((part) => {
      if (part === '[ship]' || part === '[dates]') {
        const strong = document.createElement('strong');
        strong.textContent = part === '[ship]' ? fDay.format(ship) : range;
        out.append(strong);
      } else if (part) out.append(document.createTextNode(part));
    });
  }
}
if (!customElements.get('dtn-product')) customElements.define('dtn-product', DtnProduct);

/* ---------- Product recommendations section ---------- */
class DtnRecs extends HTMLElement {
  connectedCallback() {
    if (this._ready || !this.dataset.url) return;
    this._ready = true;
    const load = async () => {
      try {
        const res = await fetch(this.dataset.url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('dtn-recs');
        // Swap in the recommendations only when there are some; otherwise keep the fallback products
        if (fresh && fresh.querySelector('.dtn-card')) this.innerHTML = fresh.innerHTML;
      } catch (e) { /* leave the fallback products */ }
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '0px 0px 600px 0px' });
      io.observe(this);
    } else load();
  }
}
if (!customElements.get('dtn-recs')) customElements.define('dtn-recs', DtnRecs);
